import { useMutation } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import { CREATE_SALES_ORDER } from 'graphql/mutations/createSalesOrder';
import { confirmCreateSalesOrderData } from 'states/common';
import { useParkzoneStore } from 'stores/parkzoneStore';
import {
  BillingType,
  CountryCode,
  Mutation,
  MutationCreateSalesOrderArgs,
  Pmc,
  Pricing,
  SalesOrderType,
} from 'types/generatedSchemaTypes';
import useCreateSalesOrderInputFactory from './useCreateSalesOrderInputFactory';
import { useUiStore } from 'stores/uiStore';

type DraftSalesOrderParams = {
  pmcId: string | undefined;
  selectedPmc: Pmc | undefined;
  startDate: Date;
  billingType: BillingType | undefined;
  unlockCode?: string;
  type?: SalesOrderType;
  automaticOnlineOrderActivation: boolean;
  isFirstMonthFree?: boolean;
  countryCode?: CountryCode;
  prices?: Pricing[];
  skipModal?: boolean;
};

const useCreateSalesOrder = () => {
  const { createInput } = useCreateSalesOrderInputFactory();

  const [createSalesOrder, { loading: loadingSalesOrder }] = useMutation<
    Mutation,
    MutationCreateSalesOrderArgs
  >(CREATE_SALES_ORDER, {
    onError: (error) => {
      if (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === 'PARKING_SPACE_NOT_AVAILABLE'
        )
      ) {
        useParkzoneStore.getState().setSelectedParkingSpace(undefined);
      }
      showToast('error.permitCreationFailed', 'error');
    },
  });

  const createDraftSalesOrder = async ({
    pmcId,
    selectedPmc,
    startDate,
    billingType,
    unlockCode,
    type = 'LTP',
    automaticOnlineOrderActivation,
    isFirstMonthFree = false,
    countryCode,
    prices,
    skipModal = false,
  }: DraftSalesOrderParams): Promise<void> => {
    const existingOrder = confirmCreateSalesOrderData();
    if (existingOrder?.salesOrderId) {
      const freshMeta = createInput(
        {
          pmcId: existingOrder.pmcId,
          startDate: existingOrder.startDate,
          selectedPmc: existingOrder.selectedPmc,
          unlockCode: existingOrder.unlockCode,
          meta: existingOrder.meta,
          isFirstMonthFree: existingOrder.isFirstMonthFree,
          countryCode: existingOrder.countryCode,
          prices: existingOrder.prices,
        },
        existingOrder.type ?? 'LTP'
      )?.[1];
      if (freshMeta) {
        confirmCreateSalesOrderData({
          ...existingOrder,
          meta: { ...existingOrder.meta, ...freshMeta },
        });
      }
      if (!skipModal) useUiStore.getState().setSheetModal('confirmPermit');
      return;
    }

    const permitData = {
      pmcId,
      startDate,
      selectedPmc,
      unlockCode,
      meta: { billingType, automaticOnlineOrderActivation },
      isFirstMonthFree,
      countryCode,
      prices,
    };
    const [permitInput, meta] = createInput(permitData, type) ?? [];
    if (!permitInput) return;

    confirmCreateSalesOrderData({ ...permitData, meta, type });

    const result = await createSalesOrder({
      variables: { input: permitInput },
    });
    const created = result.data?.createSalesOrder;
    if (!created?.uid) return;

    const orderLine = created.orderLines?.find(Boolean);
    confirmCreateSalesOrderData({
      ...permitData,
      meta,
      type,
      salesOrderId: created.uid,
      orderLineId: orderLine?.orderLineId ?? undefined,
      version: orderLine?.version ?? undefined,
      quantity: orderLine?.quantity ?? undefined,
    });

    if (!skipModal) useUiStore.getState().setSheetModal('confirmPermit');
  };

  return { createDraftSalesOrder, loadingSalesOrder };
};

export default useCreateSalesOrder;
